import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useTranslations } from 'hooks/useTranslations';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { FormInput } from 'components/molecules/FormInput';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { prepareFilters } from 'helpers/filters';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { StyledSearchForm } from 'theme/styles';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { IBatchesSearchForm } from 'models/licences';
import { useDictionary } from 'models/dictionaryContext';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { useAccount } from 'pages/AccountPage/hooks';
import { EBatcheslabels, initialBatchesSearchForm } from '../../helpers';

interface IBatchesSearchFormProps {
  onSubmit: (arg: IBatchesSearchForm) => void;
  initialFormValues?: IBatchesSearchForm;
}

const BatchesSearchForm = ({ onSubmit, initialFormValues }: IBatchesSearchFormProps) => {
  const { t } = useTranslations();
  const resolvedInitialValues = initialFormValues ?? initialBatchesSearchForm;
  const hasActiveAdvancedFilters =
    !!initialFormValues &&
    (initialFormValues.licenceAuthorityUniqueIds.length > 0 ||
      initialFormValues.statusUniqueIds.length > 0 ||
      !!initialFormValues.requestDateFrom ||
      !!initialFormValues.requestDateTo);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(hasActiveAdvancedFilters);
  const [values, setValues] = useState<IBatchesSearchForm>(resolvedInitialValues);
  const { isDesktop } = useThemeBreakpoint();
  const { account, getSelfAccountData } = useAccount();
  const {
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    weaponLicencePrintBatchStatus,
    getWeaponLicencePrintBatchStatus,
  } = useDictionary();

  useEffect(() => {
    getSelfAccountData();
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IBatchesSearchForm>({
    defaultValues: resolvedInitialValues,
  });

  useEffect(() => {
    if (!weaponLicencePrintBatchStatus) {
      getWeaponLicencePrintBatchStatus();
    }
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
  }, []);

  const initialValuesAppliedRef = useRef(false);

  useEffect(() => {
    if (initialValuesAppliedRef.current) {
      return;
    }
    if (!initialFormValues) {
      initialValuesAppliedRef.current = true;
      return;
    }
    if (!weaponLicencePrintBatchStatus || !weaponLicencingAuthority) {
      return;
    }
    initialValuesAppliedRef.current = true;
    setValues(resolvedInitialValues);
    reset(resolvedInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, weaponLicencePrintBatchStatus, weaponLicencingAuthority]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IBatchesSearchForm);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const licenceAuthorityUniqueIds = watch('licenceAuthorityUniqueIds');

  useEffect(() => {
    const hasInitialLicenceAuthority =
      !!initialFormValues && initialFormValues.licenceAuthorityUniqueIds.length > 0;
    if (
      weaponLicencingAuthority &&
      account &&
      !hasInitialLicenceAuthority &&
      !licenceAuthorityUniqueIds.length
    ) {
      const userLicenceAuthorities = weaponLicencingAuthority
        .filter((licencingAuthority) =>
          account.licenceAuthorityIds.includes(licencingAuthority.uniqueId.toString()),
        )
        .map((item) => item.uniqueId.toString());

      if (userLicenceAuthorities.length) {
        setValue('licenceAuthorityUniqueIds', userLicenceAuthorities);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weaponLicencingAuthority, account]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      if (['statusUniqueIds', 'licenceAuthorityUniqueIds'].includes(name)) {
        reset({ ...getValues(), [name]: [] });
      } else {
        reset({ ...getValues(), [name]: '' });
      }
    } else {
      reset(initialBatchesSearchForm);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          size={{ xs: 12 }}
          container
          sx={{
            alignItems: 'center',
            flexWrap: { md: 'nowrap', xs: 'wrap' },
            rowGap: 2,
            mb: 2,
          }}
          columnSpacing={2}
        >
          <Grid
            size={{ xs: 12 }}
            container
            sx={{ justifyContent: 'flex-start', flexWrap: 'nowrap', gap: 0 }}
          >
            <FormInput
              charsLimit={120}
              maxLength
              InputProps={{
                style: {
                  height: '40px',
                  margin: 0,
                  width: isDesktop ? '330px' : 'auto',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root fieldset': {
                  borderEndEndRadius: 0,
                  borderStartEndRadius: 0,
                },
              }}
              withValidation={false}
              name="search"
              withLabel={false}
              placeholder={t('global.search.button')}
              control={control}
              setValue={setValue}
              errors={errors}
              fullWidth={!isDesktop}
              id="batches_search_form_search-input"
            />
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('global.search.button')}
              type="submit"
              sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              id="batches_search_form_search-button"
            />
            <Button
              variant={EButtonVariants.outlined}
              sizePX={EButtonSizesPX.px40}
              icon={EIconTypes.filters2}
              type="submit"
              sx={{ ml: '20px' }}
              onClick={() => setShowAdvanced(!showAdvanced)}
              id="batches_search_form_filters2-button"
            />
          </Grid>
        </Grid>
        {showAdvanced && (
          <SearchWrapper>
            <StyledSearchForm
              container
              sx={{ justifyContent: 'space-between' }}
              columnSpacing={2}
              columns={10}
            >
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                  name="licenceAuthorityUniqueIds"
                  label={t(EBatcheslabels.licenceAuthorityUniqueIds)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicencePrintBatchStatus)}
                  name="statusUniqueIds"
                  label={t(EBatcheslabels.statusUniqueIds)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <FormDateTimePicker
                  name="requestDateFrom"
                  label={t(EBatcheslabels.requestDateFrom)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <FormDateTimePicker
                  name="requestDateTo"
                  label={t(EBatcheslabels.requestDateTo)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 10, md: 10, lg: 2 }}>
                <Button
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  label={t('global.search.button')}
                  type="submit"
                  fullWidth
                  alignWithInputSX
                  id="batches_search_form_advanced-search-button"
                />
              </Grid>
            </StyledSearchForm>
            <SelectedFilters
              name="batches"
              values={values}
              excludes={['perPage', 'currentPage', 'search']}
              labels={EBatcheslabels}
              handleDelete={handleResetField}
              showSaveFilters={false}
              saveRouterStateAfterClearAll={false}
              dictionaries={{
                statusUniqueIds: prepareFilters(
                  weaponLicencePrintBatchStatus,
                  'uniqueId',
                  'name',
                ),
                licenceAuthorityUniqueIds: prepareFilters(
                  weaponLicencingAuthority,
                  'uniqueId',
                  'name',
                ),
              }}
            />
          </SearchWrapper>
        )}
      </form>
    </div>
  );
};

export { BatchesSearchForm };
